// src/components/WorkbenchLayout.tsx
import React, { useEffect } from 'react';
import { FlaskConical, MessageSquare } from 'lucide-react';
import logoMini from '../assets/logo-mini.png';
import Sidebar from './Sidebar';
import TopNavBar from './TopNavBar';
import PlacedTooltipsLayer from './PlacedTooltipsLayer';
import TooltipManager from './TooltipManager';
import AiTesterPanel from './aitest/AiTesterPanel';
import CreditWarningsBanner from './CreditWarningsBanner';
import { useCaps } from '../contexts/CapabilitiesContext';
import type { ProjectCreatedPayload } from './CreateProjectModal';
import type { AppNotification } from './NotificationsPanel';

interface UserData {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  profile_picture: string | null;
  is_superuser?: boolean;
  is_staff?: boolean;
}

interface WorkbenchLayoutProps {
  children: React.ReactNode;
  activeNav?: string;
  activeProject?: number | null;
  onNavigate?: (itemId: string) => void;
  onProjectSelect?: (projectId: number) => void;
  onProjectContinue?: (project: ProjectCreatedPayload) => void;
  onLogout?: () => void;
  showRightSidebar?: boolean;
  rightSidebar?: React.ReactNode;
  userData?: UserData | null;
  selectedCurrency?: string;
  onCurrencyChange?: (currency: string) => void;
  projectsRefreshTrigger?: number;
  tooltipPageKey?: string | null;
  showTooltipManager?: boolean;
  onCloseTooltipManager?: () => void;
  onOpenTooltipManager?: () => void;
  showAiTester?: boolean;
  onCloseAiTester?: () => void;
  onOpenAiTester?: () => void;
  // Shared filter state
  projectSearch?: string;
  setProjectSearch?: (value: string) => void;
  selectedTeamFilter?: number | null;
  setSelectedTeamFilter?: (value: number | null) => void;
  selectedMarketFilter?: string;
  setSelectedMarketFilter?: (value: string) => void;
  selectedTerritoryFilter?: string;
  setSelectedTerritoryFilter?: (value: string) => void;
  selectedCategoryFilter?: string;
  setSelectedCategoryFilter?: (value: string) => void;
  selectedOccasionFilter?: string;
  setSelectedOccasionFilter?: (value: string) => void;
  onSecretProjectSeeded?: (payload: {
    projectId: number;
    name?: string;
    recipeBlueprintId?: number;
    recipeCombinationId?: number;
  }) => void;
  notifications?: AppNotification[];
  unreadCount?: number;
  notificationsOpen?: boolean;
  onToggleNotifications?: () => void;
  onCloseNotifications?: () => void;
  onOpenNotification?: (notification: AppNotification) => void;
  onMarkAllNotificationsRead?: () => void;
  onProjectsChanged?: () => void;
}

const WorkbenchLayout: React.FC<WorkbenchLayoutProps> = ({ 
  children, 
  activeNav,
  activeProject,
  onNavigate,
  onProjectSelect,
  onProjectContinue,
  onLogout,
  showRightSidebar = false,
  rightSidebar,
  userData,
  selectedCurrency,
  onCurrencyChange,
  projectsRefreshTrigger,
  tooltipPageKey = null,
  showTooltipManager = false,
  onCloseTooltipManager,
  onOpenTooltipManager,
  showAiTester = false,
  onCloseAiTester,
  onOpenAiTester,
  projectSearch,
  setProjectSearch,
  selectedTeamFilter,
  setSelectedTeamFilter,
  selectedMarketFilter,
  setSelectedMarketFilter,
  selectedTerritoryFilter,
  setSelectedTerritoryFilter,
  selectedCategoryFilter,
  setSelectedCategoryFilter,
  selectedOccasionFilter,
  setSelectedOccasionFilter,
  onSecretProjectSeeded,
  notifications,
  unreadCount,
  notificationsOpen,
  onToggleNotifications,
  onCloseNotifications,
  onOpenNotification,
  onMarkAllNotificationsRead,
  onProjectsChanged,
}) => {
  const { can, allCapabilities, loading: capsLoading } = useCaps();
  // Closed by default; staff grantAll does not unlock. Superusers always see it;
  // others only if explicitly granted tooltips_manager.
  const canManageTooltips =
    Boolean(userData?.is_superuser) ||
    (!allCapabilities && can('tooltips_manager'));
  const canUseAiTester = can('ai_prompt_testing');
  const canViewBlueprints = can('blueprints_view');
  const canViewProjects = can('projects_view');
  const canViewGos = can('gos_view');
  const canViewMarketplace = can('marketplace_view');
  const canViewOrgMenu = can('organizations_user_management_menu');

  const fallbackNav = () => {
    if (canViewMarketplace) return 'marketplace';
    if (canViewGos) return 'outcomes';
    if (canViewBlueprints || canViewProjects) return '';
    return 'profile';
  };

  useEffect(() => {
    if (capsLoading) return;
    // Default / Blueprints section is activeNav === ''
    const canStayOnBlueprintsHome =
      canViewBlueprints || (canViewProjects && Boolean(activeProject));
    if (activeNav === '' && !canStayOnBlueprintsHome) {
      onNavigate?.(fallbackNav());
      return;
    }
    if (activeNav === 'outcomes' && !canViewGos) {
      onNavigate?.(fallbackNav());
      return;
    }
    if (activeNav === 'marketplace' && !canViewMarketplace) {
      onNavigate?.(fallbackNav());
      return;
    }
    if (activeNav === 'organization' && !canViewOrgMenu) {
      onNavigate?.(fallbackNav());
    }
  }, [activeNav, activeProject, canViewBlueprints, canViewProjects, canViewGos, canViewMarketplace, canViewOrgMenu, capsLoading, onNavigate, can]);

  const handleGoHome = () => {
    if (!canViewBlueprints && !canViewProjects) {
      onNavigate?.(fallbackNav());
      return;
    }
    onProjectSelect?.(0);
    onNavigate?.('');
  };

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden">
      <div className="flex flex-shrink-0 h-12">
        <div className="w-12 flex-shrink-0 bg-[#EAE9F2] border-b border-[#E0DEE8] flex items-start justify-center pt-2">
          <button
            onClick={handleGoHome}
            className="cursor-pointer focus:outline-none"
            title="Go to projects"
            style={{ marginLeft: '5px' }}
          >
            <img
              src={logoMini}
              alt="Catalyx Canvas"
              className="object-contain"
              style={{ width: '38px', height: '32px' }}
            />
          </button>
        </div>
        <TopNavBar
          userData={userData}
          onNavigate={onNavigate}
          onGoHome={handleGoHome}
          onSecretProjectSeeded={onSecretProjectSeeded}
          notifications={notifications}
          unreadCount={unreadCount}
          notificationsOpen={notificationsOpen}
          onToggleNotifications={onToggleNotifications}
          onCloseNotifications={onCloseNotifications}
          onOpenNotification={onOpenNotification}
          onMarkAllNotificationsRead={onMarkAllNotificationsRead}
        />
      </div>
      <div className="flex flex-1 overflow-hidden">
        <Sidebar 
          activeNav={activeNav}
          activeProject={activeProject || undefined}
          onNavigate={onNavigate} 
          onProjectSelect={onProjectSelect}
          onProjectContinue={onProjectContinue}
          onLogout={onLogout}
          userData={userData}
          selectedCurrency={selectedCurrency}
          onCurrencyChange={onCurrencyChange}
          projectsRefreshTrigger={projectsRefreshTrigger}
          onProjectsChanged={onProjectsChanged}
          projectSearch={projectSearch}
          setProjectSearch={setProjectSearch}
          selectedTeamFilter={selectedTeamFilter}
          setSelectedTeamFilter={setSelectedTeamFilter}
          selectedMarketFilter={selectedMarketFilter}
          setSelectedMarketFilter={setSelectedMarketFilter}
          selectedTerritoryFilter={selectedTerritoryFilter}
          setSelectedTerritoryFilter={setSelectedTerritoryFilter}
          selectedCategoryFilter={selectedCategoryFilter}
          setSelectedCategoryFilter={setSelectedCategoryFilter}
          selectedOccasionFilter={selectedOccasionFilter}
          setSelectedOccasionFilter={setSelectedOccasionFilter}
        />
        <main className="flex-1 overflow-auto relative">
          <CreditWarningsBanner />
          {children}
        </main>
      </div>
      <PlacedTooltipsLayer pageKey={tooltipPageKey ?? null} />
      {canUseAiTester && onOpenAiTester && (
        <button
          type="button"
          onClick={onOpenAiTester}
          className="fixed bottom-24 right-10 z-[8000] flex items-center justify-center w-12 h-12 rounded-full bg-[#3da3d4] text-white shadow-lg hover:bg-[#2f8cb8] hover:shadow-xl transition-all focus:outline-none focus:ring-2 focus:ring-[#3da3d4] focus:ring-offset-2"
          title="AI prompt testing"
          aria-label="Open AI prompt testing"
        >
          <FlaskConical className="w-5 h-5" />
        </button>
      )}
      {canManageTooltips && onOpenTooltipManager && (
        <button
          type="button"
          onClick={onOpenTooltipManager}
          className={`fixed ${canUseAiTester ? 'bottom-40' : 'bottom-24'} right-10 z-[8000] flex items-center justify-center w-12 h-12 rounded-full bg-amber-500 text-white shadow-lg hover:bg-amber-600 hover:shadow-xl transition-all focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2`}
          title="Manage tooltips"
          aria-label="Manage tooltips"
        >
          <MessageSquare className="w-5 h-5" />
        </button>
      )}
      {canManageTooltips && showTooltipManager && onCloseTooltipManager && (
        <div
          data-tooltip-manager-panel
          className="fixed top-0 right-0 bottom-0 z-[10000] w-full max-w-lg bg-white shadow-2xl overflow-auto flex flex-col border-l border-gray-200"
          role="dialog"
          aria-modal
          aria-label="Manage tooltips"
        >
          <TooltipManager onClose={onCloseTooltipManager} />
        </div>
      )}
      {canUseAiTester && showAiTester && onCloseAiTester && (
        <div
          data-ai-tester-panel
          className="fixed top-0 right-0 bottom-0 z-[10000] w-full max-w-2xl bg-white shadow-2xl overflow-hidden flex flex-col border-l border-gray-200"
          role="dialog"
          aria-modal
          aria-label="AI prompt testing"
        >
          <AiTesterPanel onClose={onCloseAiTester} />
        </div>
      )}
      {showRightSidebar && rightSidebar}
    </div>
  );
};

export default WorkbenchLayout;